import { ProfileRoles } from '@bp/pim-auth-constants';
import { Grid, GridColumn, GridRow, ModalBottomButtons, Select, SelectOptionType } from '@bp/ui-components';
import { Formik } from 'formik';
import { Form } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationRolesAsString } from 'utils/organizationRolesAsString';
import { ProcuratPerson } from '../hooks/useProcuratGroups';
import { ProcuratDepartmentPersonsTableData } from '../tables/ProcuratDepartmentPersonsTable';
import { useQuery } from 'urql';
import { graphql } from 'client/gql';
import { ImportPersonsFormDataQuery } from 'client/gql/graphql';

const ImportPersonsFormQueryDocument = graphql(`
  query ImportPersonsFormData($where: ProfileWhere!) {
    profiles(where: $where) {
      uuid
      __typename
      organizationRoles
      firstName
      lastName
      email
    }
  }
`);

export type ProfileQueryType = Pick<ImportPersonsFormDataQuery, 'profiles'>['profiles'][0];

export type GrantRoleValues = Record<string, { role: ProfileRoles; assign?: string }>;

type ImportPersonsFormProps = {
  personsData: ProcuratDepartmentPersonsTableData[];
  onImport: (x: GrantRoleValues) => void;
  onClose: () => void;
};

export const ImportPersonsForm = ({ personsData, onImport, onClose }: ImportPersonsFormProps) => {
  const { t } = useTranslation();

  const [{ data, fetching }, reFetchBpProfiles] = useQuery({
    query: ImportPersonsFormQueryDocument,
    variables: {
      where: {
        AND: [
          {
            OR: [
              { email_IN: personsData.map((p) => p.email) },
              {
                AND: [
                  { lastName_IN: personsData.map((p) => p.lastName) },
                  { firstName_IN: personsData.map((p) => p.firstName) },
                ],
              },
            ],
          },
          {
            OR: [{ foreignRefId: null }, { foreignRefId: [] }],
          },
        ],
      },
    },
  });

  useEffect(() => {
    reFetchBpProfiles({ requestPolicy: 'network-only' });
  }, [personsData, reFetchBpProfiles]);

  // combine incoming data with matching profiles
  const combinedData = personsData.map((person) => {
    return { ...person, matching: findMatchingProfiles(data?.profiles, person) ?? [] };
  });

  const dataWithMatching = combinedData.filter((item) => item.matching.length > 0).sort();
  const dataWithoutMatching = combinedData.filter((item) => item.matching.length === 0).sort();

  const initialValues: GrantRoleValues = {};
  for (const person of personsData) {
    initialValues[person.id.toString()] = { role: (person.roles[0] as ProfileRoles) ?? ProfileRoles.Other };
  }

  const selectOpts: SelectOptionType[] = [
    ProfileRoles.Other,
    ProfileRoles.Student,
    ProfileRoles.Teacher,
    ProfileRoles.Parent,
  ].map((r) => ({
    label: organizationRolesAsString([r]),
    value: r,
  }));

  function findMatchingProfiles(
    profiles: ProfileQueryType[] | undefined,
    person: ProcuratPerson,
  ): ProfileQueryType[] | undefined {
    const byName = profiles?.filter((p) => p.firstName === person.firstName && p.lastName === person.lastName) ?? [];
    const byMail = profiles?.filter((p) => p.email === person.email) ?? [];
    return [...byName, ...byMail];
  }

  return (
    <Formik<GrantRoleValues> initialValues={initialValues} onSubmit={() => {}}>
      {({ setFieldValue, values, isSubmitting, errors, resetForm }) => (
        <Form>
          <Grid>
            <GridRow>
              <GridColumn width={6}>&nbsp;</GridColumn>
              <GridColumn width={2}>
                <div>{t('procuratImport.chooseRole')}</div>
                <Select
                  options={selectOpts}
                  isSearchable={true}
                  onChange={async (event) => {
                    const role = (event as SingleValue<SelectOptionType>)?.value as ProfileRoles;
                    await Promise.all(
                      personsData.map((person) =>
                        setFieldValue(person.id.toString(), {
                          ...values[person.id],
                          role,
                        }),
                      ),
                    );
                  }}
                  name={'roleSelect'}
                  dense
                  placeholder={t('procuratImport.roleForAll')}
                />
              </GridColumn>
              <GridColumn width={3}>&nbsp;</GridColumn>
            </GridRow>
            <GridRow spacingTop='none'>
              <GridColumn width={3}>&nbsp;</GridColumn>
              <GridColumn width={3}> {t('procuratImport.previouslyImportedWithRole')}</GridColumn>
              <GridColumn width={2}>&nbsp;</GridColumn>
              <GridColumn width={3} align={'start'} justify={'center'}>
                {t('procuratImport.assignExistingProfile')}
              </GridColumn>
            </GridRow>
            {dataWithMatching.map((person) => (
              <GridRow spacingTop='none' key={person.id}>
                <GridColumn width={3} align={'start'} justify={'center'}>
                  {person.firstName} {person.lastName}
                </GridColumn>
                <GridColumn width={3}> &nbsp;</GridColumn>
                <GridColumn width={2} align={'start'} justify={'center'}>
                  <Select
                    value={selectOpts.find((v) => values[person.id].role.includes(v.value as string))}
                    options={selectOpts}
                    isSearchable={true}
                    onChange={async (event) => {
                      const role = (event as SingleValue<SelectOptionType>)?.value as ProfileRoles;
                      await setFieldValue(person.id.toString(), { ...values[person.id], role });
                    }}
                    name={'roleSelect'}
                    dense
                  />
                </GridColumn>
                <GridColumn width={3} align={'start'} justify={'center'}>
                  <Select
                    options={selectOpts}
                    isSearchable={true}
                    onChange={async (event) => {
                      const uuid = (event as SingleValue<SelectOptionType>)?.value as string;
                      await setFieldValue(person.id.toString(), {
                        ...values[person.id],
                        assign: uuid,
                        role: data?.profiles.find((p) => p.uuid === uuid)?.organizationRoles ?? '',
                      });
                    }}
                    name={'assign'}
                    placeholder={t('procuratImport.assignProfile')}
                    dense
                  />
                </GridColumn>
              </GridRow>
            ))}
          </Grid>
          <hr />
          <Grid>
            {dataWithoutMatching.map((person) => {
              return (
                <GridRow spacingTop='none' key={person.id}>
                  <GridColumn width={3} align={'start'} justify={'center'}>
                    {person.firstName} {person.lastName}
                  </GridColumn>
                  <GridColumn width={3} align={'start'} justify={'center'}>
                    {selectOpts.find((v) => person.roles.includes(v.value as string))?.label}
                  </GridColumn>
                  <GridColumn width={2} align={'start'} justify={'center'}>
                    <Select
                      value={selectOpts.find((v) => values[person.id].role.includes(v.value as string))}
                      options={selectOpts}
                      isSearchable={true}
                      onChange={async (event) => {
                        const role = (event as SingleValue<SelectOptionType>)?.value as ProfileRoles;
                        await setFieldValue(person.id.toString(), { ...values[person.id], role });
                      }}
                      name={'roleSelect'}
                      dense
                    />
                  </GridColumn>
                  <GridColumn width={3}>&nbsp;</GridColumn>
                </GridRow>
              );
            })}
          </Grid>

          <ModalBottomButtons
            closeButton={{
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            submitButton={{
              text: t('procuratImport.importPersons'),
              callback: (e) => {
                resetForm();
                onClose();
                onImport(values);
              },
            }}
            isLoading={isSubmitting}
            errors={errors}
          />
        </Form>
      )}
    </Formik>
  );
};
